import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconMail, IconSend, IconTrash, IconPlus, IconEye,
  IconUsers, IconLoader2, IconDownload, IconFileImport, IconCircleCheck, IconAlertTriangle,
  IconCalendarClock, IconClock, IconX, IconSparkles,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

export default function Newsletter() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("bulletins");
  const [subs, setSubs] = useState({ items: [], total: 0, active: 0 });
  const [bulletins, setBulletins] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState({ id: null, title: "", subject: "", html_body: "", audience: "todas" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);

  // Schedule modal state
  const [scheduleFor, setScheduleFor] = useState(null); // { bid, dt }

  // AI generator state (inside editor modal)
  const [aiOpen, setAiOpen] = useState(false);
  const [aiAudience, setAiAudience] = useState("familias");
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Manual add subscriber
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [importText, setImportText] = useState("");

  // Delete confirmation targets
  const [deleteSubTarget, setDeleteSubTarget] = useState(null);
  const [deleteBulletinTarget, setDeleteBulletinTarget] = useState(null);

  // Provider diagnostics
  const [providerCfg, setProviderCfg] = useState(null);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const runProbe = async () => {
    setProbing(true); setProbeResult(null);
    try {
      const { data } = await api.post("/notifications/diagnostic-probe");
      setProbeResult(data);
    } catch (e) { setProbeResult({ ok: false, reason: formatApiError(e) }); }
    finally { setProbing(false); }
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [s, b, cfg] = await Promise.all([
        api.get(`/newsletter/subscribers?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/newsletter/bulletins"),
        api.get("/notifications/config"),
      ]);
      setSubs(s.data); setBulletins(b.data); setProviderCfg(cfg.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 3000); };

  // Role labels + inline update
  const ROLE_OPTIONS = React.useMemo(() => [
    { value: "docentes", es: "Docentes", en: "Teachers" },
    { value: "sombra", es: "Maestra Sombra", en: "Shadow Teacher" },
    { value: "psicologa", es: "Psicóloga", en: "Psychologist" },
    { value: "familia", es: "Familia", en: "Family" },
    { value: "escuelas", es: "Escuelas", en: "Schools" },
    { value: "cursos", es: "Cursos", en: "Courses" },
    { value: "otro", es: "Otro", en: "Other" },
  ], []);
  const roleLabel = (r) => {
    const o = ROLE_OPTIONS.find((x) => x.value === r);
    return o ? (lang === "es" ? o.es : o.en) : "—";
  };
  const updateSubRole = async (id, role) => {
    try { await api.patch(`/newsletter/subscribers/${id}`, { role: role || "" }); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  // ---------- Subscribers ----------
  const addSub = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    try {
      await api.post("/newsletter/subscribers", { email: newEmail, name: newName || null, role: newRole || null, source: "manual" });
      setNewEmail(""); setNewName(""); setNewRole(""); flash(lang === "es" ? "Suscriptor agregado." : "Subscriber added."); await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const importBulk = async () => {
    if (!importText.trim()) return;
    try {
      const { data } = await api.post("/newsletter/subscribers/import", { emails: importText });
      setImportText("");
      flash(lang === "es" ? `Importados: ${data.added} · Duplicados omitidos: ${data.skipped}` : `Imported ${data.added}, skipped ${data.skipped}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const removeSub = (s) => setDeleteSubTarget(s);

  const exportCSV = () => {
    const rows = [["email", "name", "role", "source", "active", "subscribed_at"], ...subs.items.map((s) => [s.email, s.name || "", s.role || "", s.source, s.active, s.subscribed_at])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `suscriptores_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Bulletins ----------
  const openNew = () => { setEditing({ id: null, title: "", subject: "", html_body: "", audience: "todas" }); setAiOpen(false); setAiTopic(""); setAiError(""); setEditorOpen(true); };
  const openEdit = (b) => { setEditing({ id: b.id, title: b.title, subject: b.subject, html_body: b.html_body, audience: b.audience || "todas" }); setAiOpen(false); setAiTopic(""); setAiError(""); setEditorOpen(true); };

  const generateWithAI = async () => {
    if (!aiTopic.trim()) {
      setAiError(lang === "es" ? "Ingresa un tema o señales para generar." : "Enter a topic or signals to generate.");
      return;
    }
    setAiError(""); setAiLoading(true);
    try {
      // Split multi-line topic into signals array
      const signals = aiTopic.split(/\n+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/ai/bulletin/draft", {
        audience: aiAudience,
        signals,
        lang,
      });
      // Backend returns {draft: "JSON string with subject + body"} — parse defensively.
      const raw = data.draft || "";
      let subject = "";
      let body = raw;
      try {
        const jsonStart = raw.indexOf("{");
        const jsonEnd = raw.lastIndexOf("}");
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          const parsed = JSON.parse(raw.slice(jsonStart, jsonEnd + 1));
          subject = parsed.subject || "";
          body = parsed.body || raw;
        }
      } catch { /* fallback: use raw text as body */ }
      // Convert plain-text body into simple <p> paragraphs when it doesn't look like HTML.
      let html = body;
      if (!/<[a-z][\s\S]*>/i.test(body)) {
        html = body
          .split(/\n{2,}/)
          .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
          .join("\n");
      }
      setEditing((prev) => ({
        ...prev,
        subject: subject || prev.subject,
        html_body: html,
        title: prev.title || subject || (lang === "es" ? "Borrador Sistema Inteligente" : "Draft AI"),
      }));
      setAiOpen(false);
      flash(lang === "es" ? "Contenido generado. Revisa y edita antes de guardar." : "Content generated. Review and edit before saving.");
    } catch (e) { setAiError(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  const saveDraft = async () => {
    setSaving(true); setError("");
    try {
      const body = { title: editing.title, subject: editing.subject, html_body: editing.html_body, audience: editing.audience || "todas" };
      if (editing.id) await api.patch(`/newsletter/bulletins/${editing.id}`, body);
      else await api.post("/newsletter/bulletins", body);
      flash(lang === "es" ? "Borrador guardado." : "Draft saved."); setEditorOpen(false); await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeBulletin = (b) => setDeleteBulletinTarget(b);

  const sendTest = async (bid) => {
    setSending(bid + "-test");
    try {
      await api.post(`/newsletter/bulletins/${bid}/test`);
      flash(lang === "es" ? "Prueba enviada a tu correo." : "Test sent to your email.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(null); }
  };

  const sendNow = async (bid) => {
    if (!window.confirm(lang === "es"
      ? `¿Enviar el boletín a los ${subs.active} suscriptores activos?`
      : `Send bulletin to ${subs.active} active subscribers?`)) return;
    setSending(bid + "-send");
    try {
      const { data } = await api.post(`/newsletter/bulletins/${bid}/send`);
      if (data.status === "sent") {
        flash(lang === "es" ? `Enviado a ${data.sent} · Fallos: ${data.failed}` : `Sent to ${data.sent}, failed ${data.failed}`);
      } else if (data.status === "partial_failure") {
        setError(
          lang === "es"
            ? `Envío parcial: ${data.sent} entregados, ${data.failed} fallaron. ${data.last_error || ""}`
            : `Partial send: ${data.sent} delivered, ${data.failed} failed. ${data.last_error || ""}`
        );
      } else {
        setError(
          lang === "es"
            ? `Ningún correo pudo entregarse. Motivo: ${data.last_error || "revisa Resend"}`
            : `No emails delivered. Reason: ${data.last_error || "check Resend"}`
        );
      }
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(null); }
  };

  const openSchedule = (bid, currentIso) => {
    // Default: next Tuesday 09:00 local time
    let dt;
    if (currentIso) {
      dt = new Date(currentIso);
    } else {
      const d = new Date();
      d.setSeconds(0, 0);
      // Advance to next Tuesday
      const daysUntilTue = (2 - d.getDay() + 7) % 7 || 7;
      d.setDate(d.getDate() + daysUntilTue);
      d.setHours(9, 0, 0, 0);
      dt = d;
    }
    // Format as "YYYY-MM-DDTHH:mm" for datetime-local input
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    setScheduleFor({ bid, dt: local });
  };

  const confirmSchedule = async () => {
    if (!scheduleFor?.bid || !scheduleFor?.dt) return;
    setSending(scheduleFor.bid + "-schedule");
    try {
      // Convert local datetime to ISO with timezone offset
      const localDt = new Date(scheduleFor.dt);
      const iso = localDt.toISOString();
      await api.post(`/newsletter/bulletins/${scheduleFor.bid}/schedule`, { scheduled_for: iso });
      flash(lang === "es" ? "Boletín programado." : "Bulletin scheduled.");
      setScheduleFor(null);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(null); }
  };

  const unschedule = async (bid) => {
    if (!window.confirm(lang === "es" ? "¿Cancelar la programación?" : "Cancel scheduling?")) return;
    setSending(bid + "-unschedule");
    try {
      await api.post(`/newsletter/bulletins/${bid}/unschedule`);
      flash(lang === "es" ? "Programación cancelada." : "Schedule cancelled.");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(null); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <>
      <div className="mod-top">
        <button className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => nav("/app")} data-testid="newsletter-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "OBSERVATORIO · BOLETÍN" : "OBSERVATORY · BULLETIN"}</span>
      </div>
      <div className="wrap-app max-w-[1080px] py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="eyebrow">{lang === "es" ? "Observatorio de Investigación" : "Research Observatory"}</p>
            <h1 className="font-display font-medium text-[clamp(24px,3vw,32px)] mt-1">
              {lang === "es" ? "Boletín semanal" : "Weekly bulletin"}
            </h1>
          </div>
          <div className="flex items-center gap-2 text-[12.5px] text-tert">
            <IconUsers size={13} /> {subs.active} {lang === "es" ? "activos" : "active"} · {subs.total} {lang === "es" ? "totales" : "total"}
          </div>
        </div>
        <p className="text-tert text-[13px] mb-6 max-w-[46em]">
          {lang === "es"
            ? "Envío recomendado: martes 9:00. Remitente: Chiessa Institute <info@chiessainstitute.com>. Puedes redactar en HTML sencillo (párrafos, listas, enlaces) y elegir entre enviar ahora o programar para más tarde."
            : "Recommended send day: Tuesday 9:00. Sender: Chiessa Institute <info@chiessainstitute.com>. Send now or schedule for later."}
        </p>

        {ok && <div className="mb-4 p-3 rounded-lg bg-okbg text-oktext text-[13px] flex items-center gap-2" data-testid="nl-ok"><IconCircleCheck size={15} /> {ok}</div>}
        {error && <div className="mb-4 p-3 rounded-lg bg-errbg text-errtext text-[13px] flex items-start gap-2" data-testid="nl-error"><IconAlertTriangle size={15} className="shrink-0 mt-0.5" /> <span>{error}</span></div>}

        {/* Provider diagnostics panel */}
        {providerCfg && (
          <div className={`mb-4 p-3 rounded-lg border ${providerCfg.provider === "resend" ? "border-teal-soft bg-teal-soft/30" : "border-honey/50 bg-honey/10"}`} data-testid="nl-provider-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="text-[12.5px]">
                <p className="font-medium">
                  {lang === "es" ? "Proveedor de correo:" : "Email provider:"}{" "}
                  <span className={providerCfg.provider === "resend" ? "text-teal-deep" : "text-honey"} data-testid="nl-provider-name">
                    {providerCfg.provider === "resend" ? "Resend ✓" : (lang === "es" ? "Modo mock (no envía)" : "Mock (does not send)")}
                  </span>
                </p>
                <p className="text-tert text-[11.5px] mt-0.5">
                  {lang === "es" ? "Remitente: " : "Sender: "}<code>{providerCfg.sender_email || "info@chiessainstitute.com"}</code>
                  {providerCfg.sender_env_configured === false && (
                    <span className="ml-2 text-honey" title={lang === "es" ? "SENDER_EMAIL no está seteado, se usa el default" : "SENDER_EMAIL not set, using default"}>
                      · {lang === "es" ? "default" : "default"}
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                className="btn-ghost btn text-[11.5px]"
                onClick={runProbe}
                disabled={probing}
                data-testid="nl-probe-btn"
              >
                {probing ? <IconLoader2 size={12} className="animate-spin" /> : <IconMail size={12} />}
                {lang === "es" ? "Probar conexión con Resend" : "Test Resend connection"}
              </button>
            </div>
            {probeResult && (
              <div className={`mt-2 p-2 rounded text-[12px] ${probeResult.ok ? "bg-okbg text-oktext" : "bg-errbg text-errtext"}`} data-testid="nl-probe-result">
                {probeResult.ok ? (
                  <span>
                    {lang === "es" ? "✓ Envío exitoso. ID Resend: " : "✓ Send OK. Resend ID: "}
                    <code>{probeResult.id}</code>. {lang === "es" ? "Revisa tu bandeja" : "Check inbox"} ({probeResult.recipient}).
                  </span>
                ) : (
                  <div>
                    <p className="font-medium">{lang === "es" ? "✗ Falló:" : "✗ Failed:"} {probeResult.reason}</p>
                    {probeResult.reason?.toLowerCase().includes("not verified") && (
                      <p className="text-[11px] mt-1">
                        {lang === "es"
                          ? "El dominio chiessainstitute.com no está aún verificado en Resend. Entra a "
                          : "The domain chiessainstitute.com is not verified in Resend yet. Go to "}
                        <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline">resend.com/domains</a>
                        {lang === "es"
                          ? ", agrega los DNS records SPF/DKIM que provee Resend y haz clic en «Verify DNS records». La propagación puede tomar unos minutos."
                          : ", add SPF/DKIM DNS records and click 'Verify DNS records'. Propagation may take a few minutes."}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="border-b border-line flex gap-1 mb-6 overflow-x-auto">
          <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === "bulletins" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`} onClick={() => setTab("bulletins")} data-testid="nl-tab-bulletins">
            {lang === "es" ? "Boletines" : "Bulletins"} ({bulletins.length})
          </button>
          <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === "subscribers" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`} onClick={() => setTab("subscribers")} data-testid="nl-tab-subs">
            {lang === "es" ? "Suscriptores" : "Subscribers"} ({subs.active})
          </button>
        </div>

        {/* ---------- Bulletins tab ---------- */}
        {tab === "bulletins" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-tert text-[12.5px]">{lang === "es" ? "Borradores + enviados" : "Drafts + sent"}</p>
              <button className="btn text-[12.5px]" onClick={openNew} data-testid="nl-new-bulletin"><IconPlus size={13} /> {lang === "es" ? "Nuevo boletín" : "New bulletin"}</button>
            </div>
            {loading ? <p className="text-tert text-[12px]">Cargando…</p> : bulletins.length === 0 ? (
              <div className="card text-center py-10"><IconMail size={22} className="mx-auto text-tert mb-2" /><p className="text-tert text-[13px]">{lang === "es" ? "Aún no hay boletines. Crea el primero." : "No bulletins yet."}</p></div>
            ) : (
              <ul className="space-y-2" data-testid="nl-bulletins-list">
                {bulletins.map((b) => (
                  <li key={b.id} className="card" data-testid={`nl-bulletin-${b.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {b.status === "sent" && (
                            <span className="tag tag-honey">{lang === "es" ? "Enviado" : "Sent"}</span>
                          )}
                          {b.status === "partial_failure" && (
                            <span className="tag" style={{ background: "#FFEDD5", color: "#9A3412" }} data-testid={`nl-bulletin-status-partial-${b.id}`}>
                              {lang === "es" ? "Parcial" : "Partial"}
                            </span>
                          )}
                          {b.status === "failed" && (
                            <span className="tag" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid={`nl-bulletin-status-failed-${b.id}`}>
                              {lang === "es" ? "Falló" : "Failed"}
                            </span>
                          )}
                          {b.status === "scheduled" && (
                            <span className="tag" style={{ background: "#EEEDFE", color: "#3C3489" }} data-testid={`nl-bulletin-status-scheduled-${b.id}`}>
                              <IconCalendarClock size={10} className="inline mr-0.5" />
                              {lang === "es" ? "Programado" : "Scheduled"}
                            </span>
                          )}
                          {(b.status === "draft" || !b.status) && (
                            <span className="tag" style={{ background: "#F3EDE1", color: "#854F0B" }}>{lang === "es" ? "Borrador" : "Draft"}</span>
                          )}
                          {b.status === "sent" && <span className="text-tert text-[11px]">→ {b.sent_to_count}/{b.recipients_total} · {fmt(b.sent_at)}</span>}
                          {b.status === "partial_failure" && (
                            <span className="text-tert text-[11px]">→ {b.sent_to_count}/{b.recipients_total} · {lang === "es" ? "algunos fallaron" : "some failed"}</span>
                          )}
                          {b.status === "failed" && (
                            <span className="text-tert text-[11px]">→ 0/{b.recipients_total} · {lang === "es" ? "ningún destinatario recibió" : "no recipients received"}</span>
                          )}
                          {b.status === "scheduled" && (
                            <span className="text-teal-deep text-[11px] font-medium" data-testid={`nl-bulletin-scheduled-for-${b.id}`}>
                              <IconClock size={10} className="inline mr-0.5" /> {fmt(b.scheduled_for)}
                            </span>
                          )}
                        </div>
                        <h3 className="font-display text-[16px] font-medium truncate">{b.title}</h3>
                        <p className="text-tert text-[12px] truncate">{b.subject}</p>
                        {(b.status === "scheduled" || b.status === "failed" || b.status === "partial_failure") && b.last_error && (
                          <p className="text-errtext text-[11px] mt-1">
                            <IconAlertTriangle size={10} className="inline mr-0.5" />
                            {lang === "es" ? "Motivo: " : "Reason: "}{b.last_error}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button className="btn-ghost btn text-[11.5px]" onClick={() => sendTest(b.id)} disabled={sending === b.id + "-test"} data-testid={`nl-test-${b.id}`}>
                          {sending === b.id + "-test" ? <IconLoader2 size={12} className="animate-spin" /> : <IconEye size={12} />} {lang === "es" ? "Test" : "Test"}
                        </button>
                        {b.status === "scheduled" && (
                          <>
                            <button
                              className="btn-ghost btn text-[11.5px]"
                              onClick={() => openSchedule(b.id, b.scheduled_for)}
                              data-testid={`nl-reschedule-${b.id}`}
                            >
                              <IconCalendarClock size={12} /> {lang === "es" ? "Reprogramar" : "Reschedule"}
                            </button>
                            <button
                              className="btn-ghost btn text-[11.5px]"
                              onClick={() => unschedule(b.id)}
                              disabled={sending === b.id + "-unschedule"}
                              data-testid={`nl-unschedule-${b.id}`}
                            >
                              <IconX size={12} /> {lang === "es" ? "Cancelar" : "Cancel"}
                            </button>
                          </>
                        )}
                        {(b.status === "draft" || !b.status || b.status === "failed" || b.status === "partial_failure") && (
                          <>
                            <button className="btn-ghost btn text-[11.5px]" onClick={() => openEdit(b)} data-testid={`nl-edit-${b.id}`}>{lang === "es" ? "Editar" : "Edit"}</button>
                            <button
                              className="btn-ghost btn text-[11.5px]"
                              onClick={() => openSchedule(b.id, null)}
                              data-testid={`nl-schedule-${b.id}`}
                            >
                              <IconCalendarClock size={12} /> {lang === "es" ? "Programar" : "Schedule"}
                            </button>
                            <button className="btn text-[11.5px]" onClick={() => sendNow(b.id)} disabled={sending === b.id + "-send"} data-testid={`nl-send-${b.id}`}>
                              {sending === b.id + "-send" ? <IconLoader2 size={12} className="animate-spin" /> : <IconSend size={12} />}
                              {(b.status === "failed" || b.status === "partial_failure")
                                ? (lang === "es" ? "Reintentar" : "Retry")
                                : (lang === "es" ? "Enviar ahora" : "Send now")}
                            </button>
                            <button className="btn-ghost btn text-[11px] px-2" onClick={() => removeBulletin(b)} aria-label="Eliminar" data-testid={`bulletin-delete-${b.id}`}><IconTrash size={12} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ---------- Subscribers tab ---------- */}
        {tab === "subscribers" && (
          <div>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <form onSubmit={addSub} className="card space-y-2">
                <p className="lbl m-0 mb-1">{lang === "es" ? "Agregar suscriptor manualmente" : "Add subscriber manually"}</p>
                <input type="email" required className="input" placeholder="email@ejemplo.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="nl-sub-email" />
                <input className="input" placeholder={lang === "es" ? "Nombre (opcional)" : "Name (optional)"} value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="nl-sub-name" />
                <select className="input" value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="nl-sub-role">
                  <option value="">{lang === "es" ? "Tipo de contacto (opcional)" : "Contact type (optional)"}</option>
                  {ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{lang === "es" ? o.es : o.en}</option>
                  ))}
                </select>
                <button type="submit" className="btn text-[12.5px]" data-testid="nl-sub-add"><IconPlus size={13} /> {lang === "es" ? "Agregar" : "Add"}</button>
              </form>
              <div className="card space-y-2">
                <p className="lbl m-0 mb-1"><IconFileImport size={13} className="inline mr-1" />{lang === "es" ? "Importar en lote" : "Bulk import"}</p>
                <textarea className="input font-mono text-[11.5px]" rows={4} placeholder={lang === "es" ? "Pega emails separados por comas, espacios o saltos de línea" : "Paste emails separated by commas, spaces or newlines"} value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="nl-sub-import-textarea" />
                <button className="btn text-[12.5px]" onClick={importBulk} data-testid="nl-sub-import-btn"><IconFileImport size={13} /> {lang === "es" ? "Importar" : "Import"}</button>
              </div>
            </div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <input className="input max-w-xs" placeholder={lang === "es" ? "Buscar…" : "Search…"} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="nl-sub-search" />
              <button className="btn-ghost btn text-[11.5px]" onClick={exportCSV} data-testid="nl-sub-export"><IconDownload size={12} /> CSV ({subs.items.length})</button>
            </div>
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="nl-sub-table">
                <thead className="bg-teal-soft/40"><tr>
                  <th className="text-left p-2 font-medium">Email</th>
                  <th className="text-left p-2 font-medium">{lang === "es" ? "Nombre" : "Name"}</th>
                  <th className="text-left p-2 font-medium">{lang === "es" ? "Tipo" : "Type"}</th>
                  <th className="text-left p-2 font-medium">{lang === "es" ? "Origen" : "Source"}</th>
                  <th className="text-left p-2 font-medium">{lang === "es" ? "Estado" : "Status"}</th>
                  <th className="text-left p-2 font-medium">{lang === "es" ? "Fecha" : "Date"}</th>
                  <th className="p-2"></th>
                </tr></thead>
                <tbody>
                  {subs.items.map((s) => (
                    <tr key={s.id} className="border-t border-line" data-testid={`nl-sub-${s.id}`}>
                      <td className="p-2 font-medium">{s.email}</td>
                      <td className="p-2 text-muted">{s.name || "—"}</td>
                      <td className="p-2">
                        <select
                          className="input text-[11.5px] py-1 px-1"
                          value={s.role || ""}
                          onChange={(e) => updateSubRole(s.id, e.target.value)}
                          data-testid={`nl-sub-role-${s.id}`}
                          aria-label={lang === "es" ? "Tipo de contacto" : "Contact type"}
                          style={{ minWidth: 120 }}
                        >
                          <option value="">{lang === "es" ? "— Sin clasificar" : "— Unclassified"}</option>
                          {ROLE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{lang === "es" ? o.es : o.en}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2"><span className="tag" style={{ fontSize: 10 }}>{s.source}</span></td>
                      <td className="p-2">{s.active ? <span className="text-oktext">✓ activo</span> : <span className="text-tert">— cancelado</span>}</td>
                      <td className="p-2 text-tert">{fmt(s.subscribed_at)}</td>
                      <td className="p-2 text-right"><button className="text-tert hover:text-errtext" onClick={() => removeSub(s)} aria-label="Eliminar" data-testid={`sub-delete-${s.id}`}><IconTrash size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subs.items.length === 0 && <p className="text-tert text-[12px] p-4 text-center">{lang === "es" ? "Sin suscriptores." : "No subscribers."}</p>}
            </div>
          </div>
        )}

        {/* ---------- Editor Modal ---------- */}
        {editorOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setEditorOpen(false)}>
            <div className="bg-paper rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="nl-editor-modal">
              <h3 className="font-display text-[18px] font-medium mb-4">{editing.id ? (lang === "es" ? "Editar boletín" : "Edit bulletin") : (lang === "es" ? "Nuevo boletín" : "New bulletin")}</h3>

              {/* AI generator toggle */}
              <div className="mb-4 p-3 rounded-lg bg-teal-soft/40 border border-teal-soft" data-testid="nl-ai-panel">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-left"
                  onClick={() => setAiOpen((v) => !v)}
                  data-testid="nl-ai-toggle"
                >
                  <span className="flex items-center gap-2 text-[13px] font-medium text-teal-deep">
                    <IconSparkles size={14} />
                    {lang === "es" ? "Generar con Sistema Inteligente" : "Generate with Smart System"}
                  </span>
                  <span className="text-tert text-[11px]">{aiOpen ? "▲" : "▼"}</span>
                </button>
                {aiOpen && (
                  <div className="mt-3 space-y-2">
                    <div className="field mb-0">
                      <label>{lang === "es" ? "Audiencia" : "Audience"}</label>
                      <select
                        className="input"
                        value={aiAudience}
                        onChange={(e) => setAiAudience(e.target.value)}
                        data-testid="nl-ai-audience"
                      >
                        <option value="todas">{lang === "es" ? "Todas (mensaje global)" : "All (global message)"}</option>
                        <option value="familias">{lang === "es" ? "Familias" : "Families"}</option>
                        <option value="docentes">{lang === "es" ? "Docentes" : "Teachers"}</option>
                        <option value="sombra">{lang === "es" ? "Maestras Sombra" : "Shadow Teachers"}</option>
                        <option value="escuelas">{lang === "es" ? "Escuelas" : "Schools"}</option>
                        <option value="psicologas">{lang === "es" ? "Psicólogas" : "Psychologists"}</option>
                      </select>
                    </div>
                    <div className="field mb-0">
                      <label>{lang === "es" ? "Tema o señales (una por línea)" : "Topic or signals (one per line)"}</label>
                      <textarea
                        className="input font-mono text-[12px]"
                        rows={4}
                        placeholder={lang === "es"
                          ? "Ej:\nAumento de dificultades de sueño en niños con TDAH esta semana.\nAlta demanda de contenido sobre adecuaciones en matemáticas."
                          : "e.g.\nRising sleep issues in ADHD kids this week.\nHigh demand for math accommodations."}
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        data-testid="nl-ai-topic"
                      />
                    </div>
                    {aiError && <p className="text-errtext text-[12px]">{aiError}</p>}
                    <button
                      type="button"
                      className="btn text-[12px]"
                      onClick={generateWithAI}
                      disabled={aiLoading || !aiTopic.trim()}
                      data-testid="nl-ai-generate"
                    >
                      {aiLoading ? <IconLoader2 size={12} className="animate-spin" /> : <IconSparkles size={12} />}
                      {lang === "es" ? "Generar contenido" : "Generate content"}
                    </button>
                    <p className="text-tert text-[11px]">
                      {lang === "es"
                        ? "Al generar, se llenará el asunto y el contenido. Puedes editarlos antes de guardar."
                        : "Generating fills subject + content. Edit before saving."}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="field mb-0">
                  <label>{lang === "es" ? "Audiencia (quién recibe este boletín)" : "Audience (who receives this bulletin)"}</label>
                  <select
                    className="input"
                    value={editing.audience || "todas"}
                    onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                    data-testid="nl-editor-audience"
                  >
                    <option value="todas">{lang === "es" ? "Todas · mensaje global a todos los suscriptores" : "All · global message to every subscriber"}</option>
                    <option value="familia">{lang === "es" ? "Familias" : "Families"}</option>
                    <option value="docentes">{lang === "es" ? "Docentes" : "Teachers"}</option>
                    <option value="sombra">{lang === "es" ? "Maestras Sombra" : "Shadow Teachers"}</option>
                    <option value="escuelas">{lang === "es" ? "Escuelas" : "Schools"}</option>
                    <option value="psicologa">{lang === "es" ? "Psicólogas" : "Psychologists"}</option>
                  </select>
                  <p className="text-tert text-[11px] mt-1">
                    {lang === "es"
                      ? "Al enviar, solo se despachará a suscriptores con este rol. 'Todas' envía a todos los activos sin filtrar."
                      : "On send, only subscribers with this role receive it. 'All' sends to every active subscriber."}
                  </p>
                </div>
                <div className="field mb-0"><label>{lang === "es" ? "Título interno" : "Internal title"}</label>
                  <input className="input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="nl-editor-title" />
                </div>
                <div className="field mb-0"><label>{lang === "es" ? "Asunto del email" : "Email subject"}</label>
                  <input className="input" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder={lang === "es" ? "Ej: Semana del … · Neuroeducación en 5 minutos" : "e.g. Week of …"} data-testid="nl-editor-subject" />
                </div>
                <div className="field mb-0"><label>{lang === "es" ? "Contenido HTML" : "HTML content"}</label>
                  <textarea className="input font-mono text-[12px]" rows={12} value={editing.html_body} onChange={(e) => setEditing({ ...editing, html_body: e.target.value })} placeholder='<p>Buen martes, comunidad Chiessa…</p><h3>Hallazgo de la semana</h3><p>…</p>' data-testid="nl-editor-body" />
                  <p className="text-tert text-[11px] mt-1">HTML simple: &lt;p&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a href=&quot;…&quot;&gt;, &lt;strong&gt;. El header con marca y el footer con unsubscribe se agregan automáticamente.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-6">
                <button className="btn-ghost btn text-[12px]" onClick={() => setEditorOpen(false)} disabled={saving}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button className="btn text-[12px]" onClick={saveDraft} disabled={saving || !editing.title || !editing.subject || !editing.html_body} data-testid="nl-editor-save">
                  {saving ? <IconLoader2 size={12} className="animate-spin" /> : null} {lang === "es" ? "Guardar borrador" : "Save draft"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ---------- Schedule Modal ---------- */}
        {scheduleFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setScheduleFor(null)}>
            <div className="bg-paper rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="nl-schedule-modal">
              <div className="flex items-center gap-2 mb-2">
                <IconCalendarClock size={18} className="text-teal-deep" />
                <h3 className="font-display text-[18px] font-medium">
                  {lang === "es" ? "Programar envío" : "Schedule send"}
                </h3>
              </div>
              <p className="text-tert text-[12.5px] mb-4">
                {lang === "es"
                  ? "Elige la fecha y hora en tu zona horaria local. El boletín se enviará automáticamente a todos los suscriptores activos."
                  : "Pick date and hour in your local timezone. Bulletin will be sent automatically to all active subscribers."}
              </p>
              <div className="field mb-4">
                <label>{lang === "es" ? "Fecha y hora" : "Date & time"}</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={scheduleFor.dt}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setScheduleFor({ ...scheduleFor, dt: e.target.value })}
                  data-testid="nl-schedule-datetime"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  className="btn-ghost btn text-[12px]"
                  onClick={() => setScheduleFor(null)}
                  disabled={sending === scheduleFor.bid + "-schedule"}
                >
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </button>
                <button
                  className="btn text-[12px]"
                  onClick={confirmSchedule}
                  disabled={!scheduleFor.dt || sending === scheduleFor.bid + "-schedule"}
                  data-testid="nl-schedule-confirm"
                >
                  {sending === scheduleFor.bid + "-schedule"
                    ? <IconLoader2 size={12} className="animate-spin" />
                    : <IconCalendarClock size={12} />}
                  {lang === "es" ? "Programar" : "Schedule"}
                </button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDeleteModal
          open={!!deleteSubTarget}
          onCancel={() => setDeleteSubTarget(null)}
          onConfirm={async () => {
            await api.delete(`/newsletter/subscribers/${deleteSubTarget.id}`);
            setDeleteSubTarget(null);
            await load();
          }}
          title={lang === "es" ? "Eliminar suscriptor" : "Delete subscriber"}
          description={
            deleteSubTarget
              ? (lang === "es"
                  ? `Se eliminará <b>${deleteSubTarget.email}</b> de la lista de suscriptores. Esta acción es irreversible.`
                  : `<b>${deleteSubTarget.email}</b> will be removed from subscribers. This is irreversible.`)
              : ""
          }
          testId="nl-sub-delete-modal"
        />
        <ConfirmDeleteModal
          open={!!deleteBulletinTarget}
          onCancel={() => setDeleteBulletinTarget(null)}
          onConfirm={async () => {
            await api.delete(`/newsletter/bulletins/${deleteBulletinTarget.id}`);
            setDeleteBulletinTarget(null);
            await load();
          }}
          title={lang === "es" ? "Eliminar boletín" : "Delete bulletin"}
          description={
            deleteBulletinTarget
              ? (lang === "es"
                  ? `Se eliminará el boletín <b>${deleteBulletinTarget.title || deleteBulletinTarget.subject || deleteBulletinTarget.id}</b>. Esta acción es irreversible.`
                  : `Bulletin <b>${deleteBulletinTarget.title || deleteBulletinTarget.subject || deleteBulletinTarget.id}</b> will be removed. This is irreversible.`)
              : ""
          }
          testId="nl-bulletin-delete-modal"
        />
      </div>
    </>
  );
}
